import React, { useEffect, useState } from 'react';
import ManoirBlason from '@/components/ui/ManoirBlason';
import { MANOIR_COURRIEL } from '@/lib/mentionsLegales';
import { SITE } from '@/constants/testIds';
import { t } from '@/lib/i18n';
import { ouvrirLeSite } from '@/lib/siteGate';
import { sitePath } from '@/lib/siteRoutes';
import { API } from '@/lib/apiBase';
import { LOGO_2026, boite } from '@/lib/logoDuManoir';

/**
 * « Bientôt » — la façade refermée tant que le Manoir n'a pas ouvert.
 *
 * ⚑ POURQUOI (Steven, 15/09/2026) : « le site doit être avec un accès, on peut
 * marquer coming soon […] par rapport à mes parents et au manoir qui arrivent
 * bientôt, c'est pas encore ouvert. » Le site est en ligne depuis le 08/09 ;
 * le Manoir, lui, n'accueille encore personne. Une vitrine qui montre huit
 * suites pour un hôtel fermé promet ce que la maison ne peut pas tenir.
 *
 * ⚑ CE QU'ELLE MONTRE, ET RIEN DE PLUS (choix de Steven, en QCM) : le bloc
 * d'identité de la maison — logo, « Résidence Hôtelière · Autun », filet,
 * « Chaque suite est une porte sur Autun » (memory/IDENTITE_MANOIR.md, verrouillé
 * le 29/07) —, une ligne, un COURRIEL, et le mot de la porte.
 *
 * ⚠ **AUCUNE DATE.** « Bientôt » ne se dément pas ; un mois annoncé se dément,
 * et se démentirait sur la première page que voient les gens à qui Steven
 * envoie l'adresse. C'est la Loi 1 appliquée à une promesse commerciale.
 *
 * ⚠ **LE LOGO NE SE REDESSINE JAMAIS**, et cette page l'a enfreint à sa
 * naissance : elle est partie avec la variante `lockup`, un tracé SVG de notre
 * cru. Steven l'a vu au premier regard — « vous avez mis le mauvais logo ».
 * C'est la seconde fois (un écu vectoriel inventé le 29/07 lui avait déjà fait
 * renvoyer son propre logo). Elle sert désormais `variant="logo"` : le FICHIER
 * du propriétaire, peint en or depuis son matte alpha.
 *
 * ⚑ NI TÉLÉPHONE NI MENTIONS LÉGALES (Steven, 15/09) : « ne mettez pas le
 * numéro de téléphone, regarde juste un e-mail » · « ne mettez pas les mentions
 * légales pour l'instant, c'est juste une page d'accueil, enlevez le lien ».
 * ⚠ Seul le LIEN s'en va : `/mentions-legales` reste servie à son adresse,
 * porte close (`App.js`), donc l'obligation LCEN n'est pas perdue — elle n'est
 * plus annoncée depuis cette page-ci.
 */
/**
 * Le paramètre qui porte la clef. `acces` et non `mot` ou `code` : le nom d'un
 * paramètre se lit dans la barre d'adresse, et « code » invite à essayer.
 */
export const ACCES_PARAM = 'acces';

/** La clef présente dans l'adresse, ou `null`. Jamais d'exception : une URL
 *  illisible est un visiteur sans clef, pas une page cassée. */
export function lireLaClef(search = typeof window !== 'undefined' ? window.location.search : '') {
    try {
        const v = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).get(ACCES_PARAM);
        return v && v.trim() ? v.trim() : null;
    } catch {
        return null;
    }
}

/** Efface la clef de la barre d'adresse, sans recharger ni ajouter d'entrée
 *  à l'historique — `replaceState`, pour que « Précédent » ne la ramène pas. */
function nettoyerLAdresse() {
    try {
        const url = new URL(window.location.href);
        url.searchParams.delete(ACCES_PARAM);
        window.history.replaceState(null, '', url.pathname + url.search + url.hash);
    } catch { /* pas d'historique accessible : le rechargement suffit */ }
}

/**
 * Présente une clef au SERVEUR. Rend l'état à afficher : `envoi` (le serveur a
 * dit oui, la page s'en va), `refus` (401) ou `panne` (tout le reste).
 *
 * ⚑ HORS DU COMPOSANT, exprès : elle ne touche aucun état de React, elle
 * interroge le serveur. Dedans, elle obligeait `useEffect` à la déclarer en
 * dépendance — et c'est `yarn build`, pas `yarn test`, qui l'a dit. *Les 4 892
 * tests passaient ; la CI aurait refusé le commit.*
 */
async function presenter(mot, lang) {
    try {
        // ⚑ `fetch` NU, et non `medardApi` — cette page est dans le tronc
        // commun (voir App.js), et `medardApi` importe **axios**. Le faire
        // voyager chez chaque visiteur qui ne verra qu'un blason et trois
        // lignes serait payer le plus lourd du paquet pour le plus léger des
        // écrans. L'adresse vient de `lib/apiBase.js` — pas recopiée.
        const r = await fetch(`${API}/medard/site/acces`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mot_de_passe: mot.trim() }),
        });
        if (!r.ok) {
            const e = new Error('refus');
            e.status = r.status;
            throw e;
        }
        // ⚠ L'adresse est NETTOYÉE avant de partir : sans cela la clef resterait
        // dans la barre du navigateur, lisible par-dessus l'épaule et recopiée
        // par mégarde dans un partage d'écran.
        nettoyerLAdresse();
        // Le serveur a dit oui : ce navigateur se souvient, et la page se
        // recharge sur le site lui-même. `assign` et non un état React — la
        // garde est lue AVANT le montage (App.js), donc c'est le rechargement
        // qui la relit.
        ouvrirLeSite();
        window.location.assign(sitePath('home', {}, lang));
        return 'envoi';
    } catch (err) {
        // ⚑ « Cette clef n'ouvre pas » et « je n'ai pas pu demander » sont deux
        // phrases différentes, et une seule est vraie pendant une panne — la
        // règle que la maison a payée sur la Clef (29/07) et sur le passeport.
        // Un 401 refuse ; tout le reste est un silence du serveur, et on
        // n'envoie pas Steven corriger un lien qui n'a rien.
        return err?.status === 401 ? 'refus' : 'panne';
    }
}

export default function SiteBientot({ lang = 'fr' }) {
    // `repos` tant que personne n'a rien tenté ; les autres états ne
    // surviennent que si l'invité est arrivé AVEC une clef dans son lien.
    const [etat, setEtat] = useState('repos'); // repos · envoi · refus · panne

    // ⚑ LA CLEF VOYAGE DANS LE LIEN, ET LA PAGE N'EN MONTRE RIEN
    // (Steven, 15/09/2026) : « le petit encart où vous mettez le mot de passe,
    // je trouve que c'est pas classe […] il faut un truc plus élégant, plus
    // discret, parce qu'on ne veut pas le donner à tout le monde ce code —
    // voilà, même carrément on l'enlève. »
    //
    // Il a raison sur les deux plans, et le second emporte le premier : un
    // champ de mot de passe affiché à tous ANNONCE qu'il existe un code. Il
    // invite à le chercher, et il enlaidit une page qui n'a que sept éléments.
    // La page ne montre donc plus rien du tout ; Steven envoie un LIEN, et le
    // lien porte la clef.
    //
    // ⚠ CE QUE CELA NE FAIT PAS, dit franchement : une clef dans une adresse
    // se retrouve dans l'historique du navigateur et dans les journaux du
    // serveur. C'est un RIDEAU, pas un coffre — exactement comme avant, car le
    // champ ne protégeait rien de plus. Ce qui protège vraiment, c'est que la
    // vérification se fait au SERVEUR : le mot n'entre jamais dans le paquet
    // JavaScript, où il serait lisible en trois clics.
    useEffect(() => {
        const clef = lireLaClef();
        if (!clef) return undefined;
        let vivant = true;
        setEtat('envoi');
        presenter(clef, lang).then((issue) => { if (vivant) setEtat(issue); });
        return () => { vivant = false; };
    }, [lang]);


    return (
        <main
            data-testid={SITE.bientot}
            className="flex min-h-screen w-full items-center justify-center bg-aman-ivory px-6 py-16"
        >
            <div className="w-full max-w-[520px] text-center">
                {/* ⚑ LE LOGO OFFICIEL, peint en or depuis son matte alpha —
                    le fichier du propriétaire, jamais un tracé de notre cru.
                    Cette page était partie avec la variante `lockup`, qui est
                    un SVG redessiné : Steven l'a vu au premier regard.

                    ⚑ ET C'EST LE LETTRAGE DE 2026 (Steven, 15/09) : le nom en
                    bas-de-casse sur une ligne, détouré de la carte qu'il a
                    envoyée. Il a choisi « le lettrage seul » — « RÉSIDENCE
                    HÔTELIÈRE » et la promesse figurent sur sa carte mais
                    restent du TEXTE ci-dessous, pour se traduire et se lire.

                    ⚠ La HAUTEUR n'est pas tapée : elle se déduit des pixels du
                    fichier. Les deux lockups n'ont pas le même rapport (1,61
                    contre 1,35) — une hauteur écrite à la main aurait laissé
                    le tracé flotter dans une boîte taillée pour l'autre. */}
                <ManoirBlason
                    variant="logo2026"
                    className="mx-auto block bg-manoir-gold"
                    style={boite(LOGO_2026, 230)}
                    title="Le Manoir d'Autun"
                />

                <p className="mt-7 text-[11px] font-medium uppercase tracking-[0.3em] text-manoir-bronze-ink">
                    {t(lang, 'brandTagline')}
                </p>

                {/* Le filet du bloc d'identité — décoratif, donc masqué au
                    lecteur d'écran : il ne dit rien qu'on doive entendre. */}
                <div aria-hidden="true" className="mx-auto mt-5 flex w-full max-w-[220px] items-center justify-center gap-2.5">
                    <span className="h-px flex-1 bg-gradient-to-r from-transparent to-manoir-gold/60" />
                    <span className="h-1.5 w-1.5 rounded-full bg-manoir-gold" />
                    <span className="h-px flex-1 bg-gradient-to-l from-transparent to-manoir-gold/60" />
                </div>

                <p className="mt-5 font-serif text-lg italic leading-snug text-manoir-ink-soft">
                    « {t(lang, 'brandPromise')} »
                </p>

                <h1 className="mt-10 font-serif text-[44px] font-light leading-[1.06] text-manoir-delft-deep [text-wrap:balance]">
                    {t(lang, 'bientotTitle')}
                </h1>
                {/* ⚑ TROIS PARAGRAPHES, ET C'EST UNE DEMANDE (Steven, 15/09) :
                    « un peu plus raffiné quand même, un peu plus long, mettre
                    un peu plus l'eau à la bouche, parce que c'est un concept
                    unique — il n'existe pas de manoir comme ça ailleurs, où
                    chaque suite est dédiée à un personnage ou un monument de
                    la ville. »

                    Le mouvement est donc : LA VILLE (ce qu'Autun garde sous
                    lui) → LA MAISON (ce que le Manoir en a fait) → MÉDARD (qui
                    vous y attend). Trois clefs plutôt qu'une longue : chaque
                    paragraphe se traduit seul, et l'on peut en retirer un sans
                    toucher aux autres.

                    ⚠ Chaque fait est SOURCÉ au canon, et deux ont été écartés
                    pour cette raison même : la capacité du théâtre (12 000 à
                    20 000 selon `canon/monuments/theatre-romain.md` — une
                    fourchette n'est pas un chiffre) et « le plus grand de
                    l'Empire », que la fiche pose en QUESTION. Une vitrine n'a
                    pas le droit d'être plus affirmative que le canon. */}
                {/* ⚠ ET LES TROIS NE PÈSENT PAS PAREIL. Trois paragraphes
                    centrés au même corps font un bloc, pas une page : l'œil
                    n'a nulle part où entrer. L'ouverture prend donc la serif
                    du titre, un cran plus grand ; les deux suivantes gardent
                    le corps de lecture. C'est la hiérarchie qui fait le
                    raffinement, pas l'ornement. */}
                <div className="mx-auto mt-6 max-w-[470px] space-y-4 text-manoir-ink-soft">
                    <p className="font-serif text-[19px] leading-[1.55] text-manoir-ink [text-wrap:pretty]">
                        {t(lang, 'bientotVille')}
                    </p>
                    <p className="text-[15px] leading-relaxed [text-wrap:pretty]">
                        {t(lang, 'bientotMaison')}
                    </p>
                    <p className="text-[15px] leading-relaxed [text-wrap:pretty]">
                        {t(lang, 'bientotMedard')}
                    </p>
                </div>

                {/* ⚑ RIEN ICI — et c'est le changement. Le champ de mot de
                    passe a disparu (Steven, 15/09). La seule chose qui peut
                    encore s'afficher est un AVEU, et seulement si quelqu'un
                    est arrivé avec une clef qui n'ouvre pas : rester muet
                    laisserait Steven croire que son lien fonctionne. */}
                {(etat === 'refus' || etat === 'panne') && (
                    <p
                        data-testid={SITE.bientotErreur}
                        role="alert"
                        className="mt-8 text-[13px] text-aman-brick"
                    >
                        {t(lang, etat === 'refus' ? 'bientotAccesErreur' : 'bientotAccesPanne')}
                    </p>
                )}

                {/* ⚑ UN COURRIEL, ET RIEN D'AUTRE (Steven, 15/09/2026 : « ne
                    mettez pas le numéro de téléphone, regarde juste un
                    e-mail »). Un téléphone sur une page publique se compose ;
                    une maison qui n'ouvre pas encore ne veut pas décrocher.
                    ⚠ Et pas de mentions légales ici non plus — « c'est juste
                    une page d'accueil ». La PAGE reste servie à son adresse
                    (`/mentions-legales`, App.js), seul le LIEN s'en va. */}
                {MANOIR_COURRIEL && (
                    <p className="mt-10 text-[13px]">
                        <a
                            href={`mailto:${MANOIR_COURRIEL}`}
                            data-testid={SITE.bientotCourriel}
                            className="inline-flex min-h-[44px] items-center gap-2 text-manoir-ink-soft underline-offset-4 hover:text-manoir-delft-deep hover:underline"
                        >
                            <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-aman-taupe-deep">
                                {t(lang, 'bientotCourrielLabel')}
                            </span>
                            {MANOIR_COURRIEL}
                        </a>
                    </p>
                )}
            </div>
        </main>
    );
}
